import React from "react";
import {CardIdentity} from "assets/icons";
import Button from "components/button";
import useModal from "components/modal/useModal";
import UseContext from "hooks/useContext";
import {formatCalendarDate} from "helper/residencePermit";

const END_LINE_KEYS = {
    valid: "valid_until",
    expiring: "expires_on",
    expired: "expired_on",
    unknown: "end_unknown",
};

const END_LINE_FALLBACKS = {
    valid: "Valable jusqu'au {date}",
    expiring: "Expire le {date}",
    expired: "Expiré le {date}",
    unknown: "Date de fin non renseignée",
};

const END_LINE_COLORS = {
    valid: "text-gray-500",
    // The project palette has no orange family: its "yellow" scale is the orange one.
    expiring: "text-yellow-600",
    expired: "text-red-600",
    unknown: "text-gray-500",
};

const DOCUMENT_TYPE_FALLBACKS = {
    new_permit: "Nouveau titre de séjour",
    receipt: "Récépissé de renouvellement",
    anef_extension: "Attestation de prolongation d'instruction (ANEF)",
};

// BadgeSquare is not reused: its texts are hardcoded and it renders nothing for '2'.
const DOCUMENT_STATUS_COLORS = {
    "1": "bg-yellow-100 text-yellow-800",
    "2": "bg-green-100 text-green-800",
    "3": "bg-red-100 text-red-800",
};

const ResidencePermitCard = ({permitStatus}) => {
    const {language} = UseContext();
    const {toggle} = useModal();
    const {state, effectiveEnd, latestDocument, canAddExtension} = permitStatus;

    const translate = (key, fallback, values = {}) =>
        Object.entries(values).reduce(
            (text, [name, value]) => text.replace(`{${name}}`, value),
            language.getValue(`residencePermit.${key}`, fallback)
        );

    const endLine = translate(END_LINE_KEYS[state], END_LINE_FALLBACKS[state], {date: formatCalendarDate(effectiveEnd)});

    const getDocumentStatusText = (document) => {
        const validated = String(document.validated);
        if (validated === "1") return translate("document_status.pending", "En cours de vérification");
        if (validated === "2") {
            const validatedEnd = formatCalendarDate(document.end_validity);
            return validatedEnd
                ? translate("document_status.validated_until", "Validé jusqu'au {date}", {date: validatedEnd})
                : translate("document_status.validated", "Validé");
        }
        return document.reason
            ? translate("document_status.refused_with_reason", "Refusé : {reason}", {reason: document.reason})
            : translate("document_status.refused", "Refusé");
    };

    return (
        <div className="bg-white rounded-lg py-3 px-4">
            <div className="flex gap-2">
                <div className="my-auto bg-gray-100 w-[40px] h-[40px] shrink-0 flex justify-center items-center rounded-full">
                    <CardIdentity wh={24} color={"#111827"}/>
                </div>
                <div className="flex flex-col my-auto">
                    <div className="ft-b text-gray-900">{translate("title", "Titre de séjour")}</div>
                    <div className={`ft-sm ${END_LINE_COLORS[state]}`}>{endLine}</div>
                </div>
            </div>
            {latestDocument && (
                <div className="flex flex-col tab:flex-row tab:items-center tab:justify-between gap-2 mt-3 pt-3 border-t border-gray-100">
                    <div className="ft-sm text-gray-700">
                        {translate(`document_types.${latestDocument.type}`, DOCUMENT_TYPE_FALLBACKS[latestDocument.type] || "")}
                    </div>
                    <div className={`self-start tab:self-auto ft-sm rounded px-2 py-1 ${DOCUMENT_STATUS_COLORS[String(latestDocument.validated)] || ""}`}>
                        {getDocumentStatusText(latestDocument)}
                    </div>
                </div>
            )}
            {canAddExtension && (
                <div className="flex tab:justify-end mt-3">
                    <Button
                        color={"PRIMARY"}
                        size={"SM"}
                        onClick={(e) => {
                            e.preventDefault();
                            toggle("j_c_visaExtension");
                        }}
                    >
                        {translate("add_extension", "Ajouter ma prolongation")}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ResidencePermitCard;
